import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Card,
  CardBody,
} from 'reactstrap';
import OfferRow from '../../components/campaign/offerRow';
import { useDispatch, useSelector } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import NoRowExistsRow from '../../components/common/noRowExistsRow';
import {
  offerListFetchData,
  redirectToOfferListPage,
  ActivateOfferData,
} from '../../actions/offerActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretSquareUp,
  faCaretSquareDown,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import sort from 'fast-sort';
import { useNavigate } from 'react-router-dom';

const SortColumns = {
  OFFER_ID: 'externalId',
  OFFER_NAME: 'name',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  STATUS: 'status',
};

const SortOrder = {
  Asc: 'Asc',
  Dsc: 'Dsc',
};

const OfferListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const offers = useSelector((state) => state.offerList);
  const isLoading = useSelector(
    (state) => state.offerListIsLoading || state.activateOfferSaving,
  );
  const hasErrored = useSelector(
    (state) => state.offerListHasErrored || state.activateOfferErrored,
  );

  const initialFormState = {
    source: '',
    offerName: '',
    startDate: null,
    endDate: null,
    status: '',
  };
  const [formState, setFormState] = useState(initialFormState);
  const [emptyRowString, setEmptyRowString] = useState('No Row Exist');
  const [isFormValid, setIsFormValid] = useState(false);
  const [sortColumn, setSortColumn] = useState(SortColumns.OFFER_ID);
  const [sortOrder, setSortOrder] = useState(SortOrder.Asc);
  const [errors, setErrors] = useState({
    source: false,
    offerName: false,
    startDate: false,
    endDate: false,
    status: false,
  });

  // Dirty check: true if any form field is changed
  const isFormDirty = useMemo(
    () =>
      Object.keys(initialFormState).some(
        (key) => formState[key] !== initialFormState[key],
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState],
  );

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = { ...errors };
    let sDate = null,
      eDate = null;

    if (formState.startDate) {
      sDate = moment(formState.startDate).format('DD-MMM-YYYY');
    }
    if (formState.endDate) {
      eDate = moment(formState.endDate).format('DD-MMM-YYYY');
    }

    if (sDate && eDate) {
      const isSameOrBefore = moment(eDate, 'DD-MMM-YYYY').isSameOrBefore(
        moment(sDate, 'DD-MMM-YYYY'),
      );
      if (isSameOrBefore) {
        alert('Invalid date');
        newErrors.startDate = true;
        newErrors.endDate = true;
      } else {
        newErrors.startDate = false;
        newErrors.endDate = false;
      }
    }

    const valid = !newErrors.startDate && !newErrors.endDate;
    setIsFormValid(valid);
    setErrors(newErrors);
    return valid;
  }, [formState, errors]);

  useEffect(() => {
    dispatch(offerListFetchData('', null, null, null, null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  // Sorting offers when offers, sortColumn, or sortOrder changes
  const sortOffers = (() => {
    const offersCopy = [...offers]; // create a shallow copy to avoid mutation
    if (sortOrder === SortOrder.Asc) {
      return sort(offersCopy).asc((o) => o[sortColumn]);
    }
    if (sortOrder === SortOrder.Dsc) {
      return sort(offersCopy).desc((o) => o[sortColumn]);
    }
  })();

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleStartDateChange = (date) => {
    setFormState((prev) => ({ ...prev, startDate: date }));
  };

  const handleEndDateChange = (date) => {
    setFormState((prev) => ({ ...prev, endDate: date }));
  };

  const handleSubmitSearchForm = (e) => {
    e.preventDefault();
    setEmptyRowString('No data has been found for the specified selection');

    const startingDate = formState.startDate
      ? moment(formState.startDate).format('DD-MMM-YYYY')
      : null;
    const endingDate = formState.endDate
      ? moment(formState.endDate).format('DD-MMM-YYYY')
      : null;

    dispatch(
      offerListFetchData(
        formState.source,
        startingDate,
        endingDate,
        formState.offerName,
        formState.status,
      ),
    );
  };

  const gotoAddNewPage = () => {
    dispatch(redirectToOfferListPage(false));
    navigate('/offers/add-offer');
  };

  const handleActivateOffer = (id) => {
    dispatch(ActivateOfferData(id));
  };

  const getOfferRows = (offersList, handleActivateOfferFn) => {
    return offersList && offersList.length > 0 ? (
      offersList.map((offer, index) => (
        <div className='panel panel-default' key={index}>
          <OfferRow offer={offer} activateOffer={handleActivateOfferFn} />
        </div>
      ))
    ) : !isLoading ? (
      <NoRowExistsRow emptyRowText={emptyRowString} />
    ) : (
      <></>
    );
  };

  const getSortButton = (column) => {
    return sortColumn === column && sortOrder === SortOrder.Asc ? (
      <span>
        <FontAwesomeIcon icon={faCaretSquareUp} />
      </span>
    ) : (
      <span>
        <FontAwesomeIcon icon={faCaretSquareDown} />
      </span>
    );
  };

  const onSort = (event, sortKey) => {
    setSortColumn(sortKey);
    setSortOrder((prev) =>
      prev === SortOrder.Asc ? SortOrder.Dsc : SortOrder.Asc,
    );
  };

  const offerRows = getOfferRows(sortOffers, handleActivateOffer);

  return (
    <div>
      <h4>Manage Offers</h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Row>
              <Col className='p-0'>
                <Form onSubmit={handleSubmitSearchForm}>
                  <div className='d-flex justify-content-left align-items-center flex-wrap gap-0'>
                    <FormGroup className='d-flex align-items-center mb-0 me-2'>
                      <Label for='source' className='mb-0 me-2'>
                        Source
                      </Label>
                      <Input
                        type='select'
                        name='source'
                        id='source'
                        onChange={handleInputChange}
                        value={formState.source}
                        className={
                          errors.source
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                      >
                        <option value=''>All</option>
                        <option value='SB'>Star Boss</option>
                        <option value='VE'>Vynamic Engage</option>
                      </Input>
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center mb-0 me-2'>
                      <Label for='offerName' className='mb-0 me-2'>
                        Offer Name
                      </Label>
                      <Input
                        id='offerName'
                        name='offerName'
                        onChange={handleInputChange}
                        value={formState.offerName}
                        className={
                          errors.offerName
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                        style={{ width: '205px' }}
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center mb-0 me-2'>
                      <Label for='startDate' className='mb-0 me-2'>
                        Start Date
                      </Label>
                      <DatePicker
                        id='startDate'
                        name='startDate'
                        selected={formState.startDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleStartDateChange}
                        className={
                          errors.startDate ? 'error mb-0' : 'form-control mb-0'
                        }
                        locale='en-au'
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center mb-0 me-2'>
                      <Label for='endDate' className='mb-0 me-2'>
                        End Date
                      </Label>
                      <DatePicker
                        id='endDate'
                        name='endDate'
                        selected={formState.endDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleEndDateChange}
                        className={
                          errors.endDate ? 'error mb-0' : 'form-control mb-0'
                        }
                        locale='en-au'
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center mb-0 me-2'>
                      <Label for='status' className='mb-0 me-2'>
                        Status
                      </Label>
                      <Input
                        type='select'
                        name='status'
                        id='status'
                        onChange={handleInputChange}
                        value={formState.status}
                        className={
                          errors.status
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                      >
                        <option value=''>All</option>
                        <option value='1'>Active</option>
                        <option value='0'>Inactive</option>
                      </Input>
                    </FormGroup>
                  </div>
                  <div className='d-flex flex-column align-items-start mt-0 gap-2'>
                    <Button
                      color='primary'
                      name='btnFilter'
                      id='btnFilter'
                      disabled={!isFormValid || isLoading || !isFormDirty}
                    >
                      <FontAwesomeIcon icon={faFilter} />
                      Filter
                    </Button>
                    {/* <Button
                      color="primary"
                      name="btnAddNew"
                      id="btnAddNew"
                      onClick={gotoAddNewPage}
                    >
                      Add New
                    </Button> */}
                  </div>
                </Form>
              </Col>
            </Row>
          </Container>
        </CardBody>
      </Card>
      <h6 className='mt-2'>Offers</h6>
      <Container fluid>
        <Row>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.OFFER_ID)}
          >
            Offer Id
            {getSortButton(SortColumns.OFFER_ID)}
          </Col>
          <Col
            md='5'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.OFFER_NAME)}
          >
            Offer Name
            {getSortButton(SortColumns.OFFER_NAME)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.START_DATE)}
          >
            Start Date
            {getSortButton(SortColumns.START_DATE)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.END_DATE)}
          >
            End Date
            {getSortButton(SortColumns.END_DATE)}
          </Col>
          <Col
            md='2'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.STATUS)}
          >
            Status
            {getSortButton(SortColumns.STATUS)}
          </Col>
          <Col md='1' className='column-header white-border'></Col>
          <Col md='1' className='column-header white-border'></Col>
        </Row>
        {offerRows}
        <Row>
          <Col>
            <BeatLoader color={'#004C5F'} loading={isLoading} />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

OfferListPage.propTypes = {
  // All state from redux
};

export default OfferListPage;
